/**
 * Invite a loan officer by NMLS. Blocks duplicates across prospects/recruits/
 * users via the server-side preflight; surfaces an existing prospect with an
 * "Open their record" affordance.
 */
import React, { useRef, useState } from 'react';
import { AlertTriangle, Eye, Send } from 'lucide-react';
import { LoDialogDescription, LoDialogShell, LoDialogTitle } from './LoRadixOverlay';
import type {
  RecruiterOption,
  InviteResult,
  InviteLoProspectInput,
  NmlsMatch,
  PricingProfileOption
} from '@/services/loProspectService';
import { LO_STAGE_META } from '@/types/lo-prospect';

const SOURCE_LABEL: Record<NmlsMatch['source'], string> = {
  lo_prospect: 'already in your pipeline',
  recruit: 'already in the Recruiting Table',
  user: 'already a Lendwise user'
};

export function InviteModal({
  recruiters,
  defaultRecruiterId,
  pricingProfiles = [],
  pricingProfilesEnabled = false,
  pricingProfilesError = false,
  onClose,
  onSubmit,
  onOpenExisting
}: {
  recruiters: RecruiterOption[];
  defaultRecruiterId?: string | null;
  pricingProfiles?: PricingProfileOption[];
  pricingProfilesEnabled?: boolean;
  pricingProfilesError?: boolean;
  onClose: () => void;
  onSubmit: (input: InviteLoProspectInput) => Promise<InviteResult>;
  onOpenExisting: (match: NmlsMatch) => void;
}) {
  const [nmls, setNmls] = useState('');
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [recruiterId, setRecruiterId] = useState(defaultRecruiterId ?? recruiters[0]?.id ?? '');
  const [pricingProfileId, setPricingProfileId] = useState('');

  const defaultPricingProfile = pricingProfiles.find((p) => p.isDefault) ?? null;
  const pricingProfilesDegraded = pricingProfilesEnabled && (pricingProfilesError || !defaultPricingProfile);
  const [err, setErr] = useState<{ type: 'required'; message: string } | { type: 'dup'; match: NmlsMatch } | null>(null);
  const [busy, setBusy] = useState(false);

  const nmlsRef = useRef<HTMLInputElement>(null);

  const submit = async () => {
    const missing: string[] = [];
    if (!nmls.trim()) {
      missing.push('NMLS ID');
    }
    if (!first.trim()) {
      missing.push('first name');
    }
    if (!last.trim()) {
      missing.push('last name');
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      missing.push('a valid email');
    }
    if (phone.replace(/[^0-9]/g, '').length < 10) {
      missing.push('a 10-digit phone');
    }
    if (missing.length > 0) {
      setErr({ type: 'required', message: `Please enter ${missing.join(', ')}.` });
      return;
    }
    setBusy(true);
    try {
      const result = await onSubmit({
        nmls,
        firstName: first,
        lastName: last,
        email,
        phone,
        recruiterId: recruiterId || null,
        pricingProfileId: pricingProfilesEnabled && !pricingProfilesDegraded ? pricingProfileId || null : null
      });
      if (result.status === 'duplicate') {
        setErr({ type: 'dup', match: result.match });
      }
      // 'created' is handled by the parent (closes the modal + toast).
    } finally {
      setBusy(false);
    }
  };

  return (
    <LoDialogShell onClose={onClose} initialFocusRef={nmlsRef}>
          <div className="modal-head">
            <div className="modal-eyebrow">Partner Pricing Preview</div>
            <LoDialogTitle>Invite a loan officer</LoDialogTitle>
            <LoDialogDescription>
              We verify the NMLS and issue 5 complimentary pricing runs. Each NMLS can only be in the pipeline once.
            </LoDialogDescription>
          </div>
          <div className="modal-body">
            <div className="field">
              <label>NMLS ID</label>
              <input
                ref={nmlsRef}
                className="nmls-input"
                placeholder="e.g. 123456"
                value={nmls}
                onChange={(e) => {
                  setNmls(e.target.value.replace(/[^0-9]/g, ''));
                  if (err) {
setErr(null);
}
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
void submit();
}
                }}
              />
            </div>

            {err?.type === 'required' && (
              <div className="err-box">
                <span className="ic">
                  <AlertTriangle />
                </span>
                <div>
                  <div className="err-title">Missing required details</div>
                  <div className="err-detail">{err.message}</div>
                </div>
              </div>
            )}
            {err?.type === 'dup' && (
              <div className="err-box">
                <span className="ic">
                  <AlertTriangle />
                </span>
                <div>
                  <div className="err-title">Already on file</div>
                  <div className="err-detail">
                    NMLS #{nmls} belongs to <b>{err.match.name ?? 'an existing record'}</b>
                    {err.match.city ? ` · ${err.match.city}, ${err.match.state ?? ''}` : ''} — {SOURCE_LABEL[err.match.source]}
                    {err.match.source === 'lo_prospect' && err.match.stage
                      ? ` (currently ${LO_STAGE_META[err.match.stage].label})`
                      : ''}
                    {err.match.source === 'recruit' && err.match.status ? ` (${err.match.status})` : ''}.
                  </div>
                  {err.match.source === 'lo_prospect' && (
                    <button className="err-link" onClick={() => onOpenExisting(err.match)} type="button">
                      <Eye />
                      Open their record
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="modal-grid">
              <div className="field">
                <label>First name</label>
                <input value={first} onChange={(e) => setFirst(e.target.value)} />
              </div>
              <div className="field">
                <label>Last name</label>
                <input value={last} onChange={(e) => setLast(e.target.value)} />
              </div>
            </div>
            <div className="modal-grid">
              <div className="field">
                <label>Email</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@email.com" />
              </div>
              <div className="field">
                <label>Phone</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(480) 555-0142" inputMode="tel" />
              </div>
            </div>
            <div className="field">
              <label>Assign recruiter</label>
              <select aria-label="Assign recruiter" value={recruiterId} onChange={(e) => setRecruiterId(e.target.value)}>
                <option value="">Unassigned</option>
                {recruiters.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
            {pricingProfilesEnabled && (
              <div className="field">
                <label>Pricing Profile</label>
                {pricingProfilesDegraded ? (
                  <select aria-label="Pricing Profile" value="" disabled>
                    <option value="">Pricing profiles unavailable</option>
                  </select>
                ) : (
                  <select aria-label="Pricing Profile" value={pricingProfileId} onChange={(e) => setPricingProfileId(e.target.value)}>
                    <option value="">{defaultPricingProfile?.label}</option>
                    {pricingProfiles
                      .filter((p) => !p.isDefault)
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.label}
                        </option>
                      ))}
                  </select>
                )}
              </div>
            )}
          </div>
          <div className="modal-foot">
            <button className="btn-sm" onClick={onClose} type="button">
              Cancel
            </button>
            <button className="btn-sm solid" onClick={() => void submit()} disabled={busy} type="button">
              <Send size={15} />
              {busy ? 'Sending…' : 'Send invite'}
            </button>
          </div>
    </LoDialogShell>
  );
}
